import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useFinance } from './financeContext';

// Per-list-page state: URL-synced page / limit / q / status / vertical / dateFrom / dateTo /
// minAmount / maxAmount / overdue / hasReminders. Debounces q at 350ms (matches DeliveryList).
// `filterKeys` lets a page opt into only the filters it supports.
const DEFAULT_LIMIT = 25;
export const PAGE_SIZES = [25, 50, 100];

export const ALL_FILTER_KEYS = [
  'status', 'vertical', 'dateFrom', 'dateTo', 'minAmount', 'maxAmount', 'overdue', 'hasReminders',
  'ownerId', 'method'
];

export default function useFinanceQuery(loader, { filterKeys = ALL_FILTER_KEYS, sig: extraSig = '' } = {}) {
  const { scopedVertical } = useFinance();
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, hasMore: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [qDraft, setQDraft] = useState(params.get('q') || '');
  const debounceRef = useRef();

  const get = useCallback((k) => params.get(k) || '', [params]);
  const page = Math.max(1, Number(get('page')) || 1);
  const limit = Number(get('limit')) || DEFAULT_LIMIT;

  const writeMany = useCallback((patch, { resetPage = true } = {}) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(patch).forEach(([k, v]) => {
        if (v === '' || v == null || v === false) next.delete(k);
        else next.set(k, String(v));
      });
      if (resetPage && !('page' in patch)) next.delete('page');
      return next;
    }, { replace: true });
  }, [setParams]);

  // Debounce the free-text box into the URL.
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if ((params.get('q') || '') !== qDraft) writeMany({ q: qDraft.trim() });
    }, 350);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qDraft]);

  const activeFilterCount = filterKeys.filter((k) => {
    // The vertical filter is not user-controllable when the module is scoped to one vertical.
    if (k === 'vertical') return !scopedVertical && !!get('vertical');
    if (k === 'minAmount' || k === 'maxAmount') return false; // counted once below
    return !!get(k);
  }).length + ((get('minAmount') || get('maxAmount')) ? 1 : 0);

  const apiParams = useMemo(() => {
    const p = { page, limit };
    const q = get('q');
    if (q) p.q = q;
    filterKeys.forEach((k) => {
      const v = get(k);
      if (v) p[k] = v;
    });
    // Embedded in a vertical admin app: force the vertical scope, overriding any URL value, so a
    // list can never surface another vertical's rows.
    if (scopedVertical) p.vertical = scopedVertical;
    return p;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.toString(), filterKeys.join(','), extraSig, scopedVertical]);

  const reload = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await loader(apiParams);
      setRows(res.data || []);
      setPagination(res.pagination || { page, total: (res.data || []).length, hasMore: false });
    } catch (err) {
      setError(err?.response?.data?.msg || 'Could not load this list.');
      setRows([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiParams]);

  useEffect(() => { reload(); }, [reload]);

  const totalPages = pagination.total && limit
    ? Math.max(1, Math.ceil(pagination.total / limit))
    : (pagination.hasMore ? page + 1 : page);

  const clearFilters = useCallback(() => {
    setQDraft('');
    writeMany(Object.fromEntries([...ALL_FILTER_KEYS, 'q'].map((k) => [k, ''])));
  }, [writeMany]);

  return {
    rows, pagination, loading, error, reload,
    page, limit, totalPages,
    get, setFilter: (k, v) => writeMany({ [k]: v }),
    setPage: (n) => writeMany({ page: n }, { resetPage: false }),
    setLimit: (n) => writeMany({ limit: n }),
    qDraft, setQDraft,
    activeFilterCount, clearFilters,
    syntheticPagination: !!pagination.synthetic
  };
}
